import React from 'react';

import StyledComponent from '~/components/Styled/Styled';
import ScrollMenu from '~/components/ScrollMenu/ScrollMenu';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import ScrollMenuElement from '~/components/ScrollMenuElement/ScrollMenuElement';

import ResultsMap from '~/constants/ResultsMap';

import getOr from '~/util/getOr';

import css from './RacingResultsMatchupCard.scss';

type Props = {
  event: any;
  eventRecords: any;
};

type State = {
  navElementSelected: any;
  eventId: any;
};

class RacingResultsMatchupCard extends React.Component<Props, State> {
  static displayName = 'RacingResultsMatchupCard';

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.event.id === prevState.eventId) {
      return null;
    }
    const leagueSlug = getOr(nextProps, 'event.league.slug', '');
    const defaultNavElement = getOr(
      ResultsMap,
      `[${leagueSlug}].matchupCards.eventElements[0]`,
      null
    );
    return {
      navElementSelected: defaultNavElement,
      eventId: nextProps.event.id,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      navElementSelected: null,
      eventId: null,
    };
  }

  eventStatus = getOr(this.props, 'event.event_status', '');
  leagueSlug = getOr(this.props, 'event.league.slug', '');

  menuElementClicked(navElementSelected) {
    this.setState({ navElementSelected });
  }

  renderMenuElements(eventNavElements) {
    return (
      <ScrollMenu>
        {eventNavElements.map(navElement => {
          if (navElement.event_status.includes(this.eventStatus)) {
            return (
              <div key={navElement.label}>
                <ScrollMenuElement
                  name={navElement.label}
                  selected={
                    navElement.label === getOr(this.state.navElementSelected, 'label', null)
                  }
                  onClick={() => {
                    this.menuElementClicked(navElement);
                  }}
                />
              </div>
            );
          }
          return null;
        })}
      </ScrollMenu>
    );
  }

  renderPlayerRecordHeaders() {
    const playerHeader = getOr(ResultsMap, `[${this.leagueSlug}].player.label`, null);
    // reversing a shallow copy of the statHeaders because 'float: right' is applied sequentially
    const statHeaders = (getOr(this.state.navElementSelected, 'stats') || []).slice(0).reverse();
    return (
      <div className={css.headerContainer}>
        <div className={css.playerHeader}>{playerHeader}</div>
        {statHeaders.map(stat => (
          <div className={css.statHeader} key={stat.label}>
            {stat.label}
          </div>
        ))}
      </div>
    );
  }

  renderPlayerRecords() {
    const recordType = getOr(this.state.navElementSelected, 'property', null);
    if (!recordType) {
      return null;
    }
    const recordKey = `${this.leagueSlug.toLowerCase()}/${this.state.eventId}/${recordType}`;
    const recordData = getOr(this.props, `eventRecords[${recordKey}]`, []);
    const playerProperty = ResultsMap[this.leagueSlug].player.property;
    // reversing a shallow copy of the statHeaders because 'float: right' is applied sequentially
    const statHeaders = (getOr(this.state.navElementSelected, 'stats') || []).slice(0).reverse();
    return recordData.map(playerRecord => {
      const name = getOr(playerRecord, `${playerProperty}.full_name`, '');
      const playerNumber = getOr(playerRecord, `${playerProperty}.car.number`, null);
      const rank = playerRecord.position || playerRecord.rank || playerRecord.starting_position;
      return (
        <div className={css.playerRecordContainer} key={playerRecord.id}>
          <div className={css.playerRank}>{rank}</div>
          <div className={css.playerName}>{name}</div>
          {playerNumber && <div className={css.playerNumber}>&nbsp;({playerNumber})</div>}

          {statHeaders.map(stat => {
            const pad = num => `0${num}`.slice(-2);
            let statValue = getOr(playerRecord, stat.property, '-');
            if (stat.type === 'time' && statValue !== '-') {
              let timeInSeconds = statValue;
              const minutes = Math.floor(timeInSeconds / 60);
              timeInSeconds %= 60;
              const seconds = Math.floor(timeInSeconds);
              timeInSeconds -= seconds;
              const milliseconds = Math.floor(timeInSeconds * 100);
              statValue = `${minutes}:${pad(seconds)}:${pad(milliseconds)}`;
            }
            return (
              <div className={css.stat} key={stat.label}>
                {statValue}
              </div>
            );
          })}
        </div>
      );
    });
  }

  render() {
    const eventRecords = getOr(this.props, 'eventRecords', {});
    if (
      // eslint-disable-next-line no-prototype-builtins
      !eventRecords.hasOwnProperty(`${this.leagueSlug}/${this.state.eventId}/driver_records`) ||
      // eslint-disable-next-line no-prototype-builtins
      !eventRecords.hasOwnProperty(`${this.leagueSlug}/${this.state.eventId}/event_drivers`)
    ) {
      return (
        <div className={css.pregameNoticeContainer}>
          <div className={css.fetchingDataSpinner}>
            <LoadingSpinner SpinnerType="square_flip" />
          </div>
          <div className={css.preGameNoticeText1}>Please wait</div>
          <div className={css.preGameNoticeText2}>Fetching your data</div>
        </div>
      );
    }
    if (this.eventStatus === 'pre_game') {
      return (
        <div className={css.pregameNoticeContainer}>
          <div className={css.preGameNoticeText1}>Event hasn&apos;t started yet</div>
          <div className={css.preGameNoticeText2}>Please check back later for more details</div>
        </div>
      );
    }
    const eventNavElements = getOr(
      ResultsMap,
      `[${this.leagueSlug}].matchupCards.eventElements`,
      []
    );
    return (
      <div className={css.matchupRecordsContainer}>
        {!!eventNavElements.length && this.renderMenuElements(eventNavElements)}
        {this.renderPlayerRecordHeaders()}
        {this.renderPlayerRecords()}
      </div>
    );
  }
}

export default StyledComponent(RacingResultsMatchupCard, [css]);
